import React, {useEffect, use, createContext, useState} from "react";
import PropTypes from "prop-types";
import {useQuery} from "@apollo/client";
import {GET_ORGANIZATION_SETTINGS} from "../graphql/organizationQueries";
import {Loader} from "../components";
import {usePermissions} from "./AuthzProvider";
import {
  processExactKeyValuePairSettings,
  processJsonObjectSettings,
  processRolesAndUsersSettings,
  processTrueFalseSettings,
  processIdsSettings,
} from "../resources/orgSettings";
import {findWhere, isEmpty} from "underscore";

export const OrgSettingsContext = createContext(null);

export const OrgSettingsProvider = ({children}) => {
  const [orgSettings, setSettings] = useState({});
  const {canReadRegulatoryResearch} = usePermissions();

  const {data: settings, loading: settingsLoading} = useQuery(GET_ORGANIZATION_SETTINGS);

  useEffect(() => {
    if (settings?.organizationSettings) {
      const timeframe = findWhere(settings.organizationSettings, {
        key: "complianceTimeframe",
      });
      const durationObject = JSON.parse(timeframe.value);
      const newUserRoleIdsSetting = findWhere(settings.organizationSettings, {
        key: "newUserRoleIds",
      });
      const defaultLeadershipChatAccessSetting = findWhere(settings.organizationSettings, {
        key: "defaultLeadershipChatAccess",
      });
      const defaultIncidentChatOmissionsSetting = findWhere(settings.organizationSettings, {
        key: "defaultIncidentChatOmissions",
      });
      const defaultScratchPadAccessSetting = findWhere(settings.organizationSettings, {
        key: "defaultScratchPadAccess",
      });
      const responseWorkflowName = findWhere(settings.organizationSettings, {
        key: "responseWorkflowName",
      });
      const workflowName = JSON.parse(responseWorkflowName.value);

      const aiPermissionsSetting = findWhere(settings.organizationSettings, {
        key: "aiPermissions",
      });
      const aiPermissions = processJsonObjectSettings(aiPermissionsSetting);

      const aiModelInferenceProfileSetting = findWhere(settings.organizationSettings, {
        key: "aiModelInferenceProfile",
      });

      const trustedAdvisorModelProfileSetting = findWhere(settings.organizationSettings, {
        key: "trustedAdvisorModelProfile",
      });

      const keyValuePairSettings = processExactKeyValuePairSettings(settings.organizationSettings, [
        "actionVisibility",
        "complianceCommunicationChannels",
        "conversationsLegalDisclaimer",
        "notificationSignature",
        "orgFullname",
      ]);
      const trueFalseSettings = processTrueFalseSettings(settings.organizationSettings, [
        "MFARequired",
        "newIncidentsRestricted",
        "autoApproveIncidents",
        "attributeTabOnActions",
        "expandActivityEditor",
        "metricsInIncidents",
        "moreActivitiesOnEvents",
        "editActivitiesAllowed",
        "enableIncidentLeadershipChat",
        "enableIncidentChat",
        "enableScratchPad",
        "disableMobileScreenCapture",
        "canDownloadDocuments",
        "notificationCenterEnabled",
        "autoRecordMeetings",
        "autoTranscribeMeetings",
        "autoSummarizeMeetings",
      ]);

      const closedIncidentsReadOnlyPermissionsSetting = findWhere(settings.organizationSettings, {
        key: "closedIncidentsReadOnlyPermissions",
      });
      const closedIncidentsReadOnlyPermissions = processJsonObjectSettings(
        closedIncidentsReadOnlyPermissionsSetting,
      );

      const activityCreateWhenSetting = findWhere(settings.organizationSettings, {
        key: "activityCreateWhen",
      });
      const activityCreateWhen = processJsonObjectSettings(activityCreateWhenSetting);

      const activityUpdateWhenSetting = findWhere(settings.organizationSettings, {
        key: "activityUpdateWhen",
      });
      const activityUpdateWhen = processJsonObjectSettings(activityUpdateWhenSetting);

      const orgSettings = {
        ...keyValuePairSettings,
        ...trueFalseSettings,
        entitlements: settings.organizationEntitlements,
        complianceTimeframeSetting: timeframe,
        [timeframe.key]: durationObject.months + " months",
        newUserRoleIdsSetting,
        newUserRoleIds: processIdsSettings(newUserRoleIdsSetting),
        defaultLeadershipChatAccess: processRolesAndUsersSettings(
          defaultLeadershipChatAccessSetting,
        ),
        defaultLeadershipChatAccessSetting,
        defaultIncidentChatOmissions: processRolesAndUsersSettings(
          defaultIncidentChatOmissionsSetting,
        ),
        defaultIncidentChatOmissionsSetting,
        defaultScratchPadAccess: processRolesAndUsersSettings(defaultScratchPadAccessSetting),
        defaultScratchPadAccessSetting,
        [responseWorkflowName.key]: {
          upper: workflowName.workflowName,
          lower: workflowName.workflowNameLower,
        },
        responseWorkflowNameSetting: responseWorkflowName,
        aiPermissionsSetting,
        aiPermissions,
        aiModelInferenceProfile: aiModelInferenceProfileSetting?.value,
        aiModelInferenceProfileSetting,
        trustedAdvisorModelProfile: trustedAdvisorModelProfileSetting?.value,
        closedIncidentsReadOnlyPermissions,
        closedIncidentsReadOnlyPermissionsSetting,
        activityCreateWhenSetting,
        activityCreateWhen,
        activityUpdateWhenSetting,
        activityUpdateWhen,
      };

      setSettings(orgSettings);
    }
  }, [settings]);

  const showRegulatoryResearch = Boolean(
    canReadRegulatoryResearch && orgSettings.entitlements?.regulatoryResearch,
  );
  const providerValues = {...orgSettings, settingsLoading, showRegulatoryResearch};

  if (isEmpty(orgSettings)) {
    return <Loader fullViewingHeight />;
  }

  return <OrgSettingsContext value={providerValues}>{children}</OrgSettingsContext>;
};

export const useOrgSettings = () => {
  const context = use(OrgSettingsContext);
  if (!context) {
    throw new Error("Context must be used within the correct provider");
  }
  return context;
};

OrgSettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
